import { useState, useEffect, useRef } from 'react';
import {
  getOvenConfig,
  finishBatch,
  RoastBatch,
  OvenQueues,
  SensorReading,
} from '../api/client';
import { useAppData } from '../context/AppDataContext';
import { playTimerDoneSound, resumeAudioContext } from '../utils/alerts';
import { ovenQueueHead } from '../utils/ovenQueue';

const OVEN_IDS = ['1', '2'];
const SENSOR_STALE_MS = 90_000;

interface LatestSensorValue {
  value: number | null;
  status: 'live' | 'unassigned' | 'missing' | 'stale';
}

function latestSensorValue(
  readings: SensorReading[],
  deviceId: string | null | undefined,
  address: string,
  name: string,
  nowMs: number,
): LatestSensorValue {
  if (!deviceId) return { value: null, status: 'unassigned' };

  let latest: { value: number; timeMs: number } | null = null;
  for (const reading of readings) {
    if (
      reading.device_id !== deviceId
      || (reading.register_address !== address && reading.register_name !== name)
    ) {
      continue;
    }
    const value = parseFloat(reading.value);
    const timeMs = Date.parse(reading.time);
    if (!Number.isFinite(value) || !Number.isFinite(timeMs)) continue;
    if (!latest || timeMs > latest.timeMs) latest = { value, timeMs };
  }

  if (!latest) return { value: null, status: 'missing' };
  if (nowMs - latest.timeMs > SENSOR_STALE_MS) return { value: null, status: 'stale' };
  return { value: latest.value, status: 'live' };
}

function formatBeanTemperature(reading: LatestSensorValue): string {
  if (reading.status === 'live' && reading.value != null) {
    return `${Math.round(reading.value)}°C`;
  }
  if (reading.status === 'unassigned') return 'לא משויך';
  if (reading.status === 'stale') return 'מנותק';
  return '—';
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  const clamped = Math.max(0, seconds);
  const m = Math.floor(clamped / 60);
  const s = Math.round(clamped % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Live remaining seconds — computed from start timestamp so the countdown ticks smoothly
// between server polls and persists even after timer_running flips to false.
function liveRemaining(batch: RoastBatch, nowMs: number): number | null {
  if (batch.timer_started_at && batch.timer_duration_seconds != null) {
    const startedMs = new Date(batch.timer_started_at).getTime();
    const elapsed = (nowMs - startedMs) / 1000;
    return Math.max(0, batch.timer_duration_seconds - elapsed);
  }
  return batch.remaining_seconds ?? null;
}

function StatusPill({ label, color }: { label: string; color: string }) {
  return <span className={`px-4 py-1.5 rounded-full text-lg font-bold ${color}`}>{label}</span>;
}

function CountChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex-1 bg-black/20 backdrop-blur-sm rounded-xl p-2 text-center">
      <div className={`text-2xl font-extrabold ${color}`}>{value}</div>
      <div className="text-sm text-white/60 mt-1">{label}</div>
    </div>
  );
}

function OvenMetrics({
  remaining,
  beanTemp,
  timerColor,
}: {
  remaining: number | null;
  beanTemp: LatestSensorValue;
  timerColor: string;
}) {
  return (
    <div className="relative z-10 flex flex-1 flex-col justify-center gap-6 rounded-2xl bg-black/20 px-4 py-6 text-center">
      <div>
        <div className="mb-2 text-lg font-semibold text-white/70">זמן נותר</div>
        <div className={`text-[clamp(4rem,10vw,9rem)] font-black tabular-nums leading-none ${timerColor}`} dir="ltr">
          {formatDuration(remaining)}
        </div>
      </div>
      <div className="border-t border-white/20 pt-6">
        <div className="mb-2 text-lg font-semibold text-white/70">טמפרטורת פולים · BT</div>
        <div
          className={`${beanTemp.status === 'live' ? 'text-[clamp(3rem,7vw,6rem)]' : 'text-3xl'} font-black tabular-nums leading-none text-white`}
          dir="auto"
        >
          {formatBeanTemperature(beanTemp)}
        </div>
      </div>
    </div>
  );
}

function OvenCard({
  mid,
  batches,
  ovenQueues,
  beanTemp,
  nowMs,
  onFinish,
}: {
  mid: string;
  batches: RoastBatch[];
  ovenQueues: OvenQueues;
  beanTemp: LatestSensorValue;
  nowMs: number;
  onFinish?: () => void;
}) {
  const ovenBatches = batches.filter((b) => b.machine_id === mid);
  const active = ovenBatches.find((b) => b.status === 'in-progress') ?? null;
  const nextBatch = ovenQueueHead(ovenQueues, mid, batches);
  const isReady = !active && !!nextBatch;
  const doneCount = ovenBatches.filter((b) => b.status === 'done').length;
  const inProgressCount = ovenBatches.filter((b) => b.status === 'in-progress').length;
  const queuedCount = ovenBatches.filter((b) => b.status === 'planned').length;

  const isRoasting = active?.timer_running === true;
  const isWaiting = !!active && !active.timer_running;

  const remaining = active ? liveRemaining(active, nowMs) : null;
  const duration = active?.timer_duration_seconds ?? null;
  const progress =
    remaining != null && duration ? Math.min(1, Math.max(0, 1 - remaining / duration)) : 0;

  // Timer alarm states — isDone latches once elapsed >= duration, regardless of timer_running.
  const timerStarted = !!active?.timer_started_at;
  const isDone   = timerStarted && remaining !== null && remaining <= 0;
  const nearDone = isRoasting && !isDone && remaining != null && remaining <= 60;

  const cardBg = isDone
    ? 'bg-gradient-to-br from-red-700 via-red-600 to-orange-700'
    : isRoasting
    ? nearDone
      ? 'bg-gradient-to-br from-red-600 via-orange-600 to-red-700'
      : 'bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600'
    : isWaiting
    ? 'bg-gradient-to-br from-yellow-400 via-amber-400 to-yellow-500'
    : isReady
    ? 'bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900'
    : 'bg-gradient-to-br from-gray-800 to-gray-900';

  const textPrimary = (active || isReady) ? 'text-white' : 'text-gray-300';
  const textMuted = (active || isReady) ? 'text-white/70' : 'text-gray-500';
  const innerBg = (active || isReady) ? 'bg-black/20' : 'bg-black/30';
  const timerColor = nearDone ? 'text-yellow-200' : 'text-white';

  // Play alarm sound immediately when done, then repeat every 12 s until batch is finished.
  const alarmFiredRef = useRef(false);
  useEffect(() => {
    if (!isDone) { alarmFiredRef.current = false; return; }
    if (!alarmFiredRef.current) {
      alarmFiredRef.current = true;
      resumeAudioContext();
      playTimerDoneSound().catch(() => {});
    }
    const id = setInterval(() => playTimerDoneSound().catch(() => {}), 12000);
    return () => clearInterval(id);
  }, [isDone]);

  // ── ROASTING / DONE view ──────────────────────────────────────────────────
  if (isRoasting || isDone) {
    return (
      <div className={`${cardBg} rounded-3xl p-4 flex flex-col gap-3 min-h-0 relative overflow-hidden transition-all duration-700 shadow-2xl ${isDone ? 'animate-pulse' : ''}`}>
        {/* Pulsing ring */}
        <div
          className={`absolute inset-0 rounded-3xl pointer-events-none ${nearDone || isDone ? 'animate-pulse' : ''}`}
          style={{ boxShadow: isDone ? '0 0 0 8px rgba(255,80,80,0.6)' : nearDone ? '0 0 0 6px rgba(255,255,150,0.5)' : '0 0 0 4px rgba(255,200,100,0.3)' }}
        />

        {/* Oven and batch details */}
        <div className="flex items-center justify-between shrink-0 z-10">
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-3 h-3 rounded-full ${isDone ? 'bg-yellow-200 animate-ping' : nearDone ? 'bg-yellow-300 animate-ping' : 'bg-white animate-ping'}`} />
              <span className="text-2xl font-black text-white">תנור {mid}</span>
            </div>
            <span className="mt-1 max-w-[430px] truncate text-4xl font-bold leading-tight text-white sm:text-5xl" dir="auto" title={active?.blend_name}>{active?.blend_name}</span>
            {active?.loading_worker_name && (
              <span className="text-sm font-bold text-teal-100">👤 העמסה: {active.loading_worker_name}</span>
            )}
            {active?.timer_start_source === 'sensor' && (
              <span className="mt-1 self-start rounded-full border border-cyan-100/50 bg-cyan-900/35 px-3 py-1 text-sm font-bold text-cyan-50">
                📡 הופעל אוטומטית מחיישן
              </span>
            )}
          </div>
        </div>

        <OvenMetrics remaining={remaining} beanTemp={beanTemp} timerColor={nearDone || isDone ? 'text-yellow-200 animate-pulse' : timerColor} />

        {/* Done: finish button */}
        {isDone && onFinish && (
          <button
            onClick={onFinish}
            className="z-10 w-full py-4 bg-yellow-300 text-red-900 rounded-2xl text-2xl font-black hover:bg-yellow-200 active:scale-95 touch-manipulation animate-bounce"
          >
            🚪 סיום קלייה
          </button>
        )}
      </div>
    );
  }

  // ── IDLE / READY view ─────────────────────────────────────────────────────
  return (
    <div className={`${cardBg} rounded-3xl p-4 flex flex-col gap-3 min-h-0 relative overflow-hidden transition-all duration-700 shadow-2xl`}>

      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            {isWaiting && (
              <span className="inline-block w-4 h-4 rounded-full bg-yellow-900/40 animate-pulse" />
            )}
            {isReady && (
              <span className="inline-block w-4 h-4 rounded-full bg-blue-400 animate-pulse" />
            )}
            <h2 className={`text-2xl font-black tracking-tight ${textPrimary}`}>תנור {mid}</h2>
          </div>
        </div>
        {active ? (
          <StatusPill label="⏳ ממתין" color="bg-black/20 text-white border border-white/20 text-xl px-5 py-2" />
        ) : isReady ? (
          <StatusPill label="🟢 מוכן לקלייה" color="bg-blue-400/20 text-blue-200 border border-blue-300/40 text-xl px-5 py-2" />
        ) : (
          <StatusPill label="פנוי ✓" color="bg-white/10 text-gray-400 border border-gray-600 text-xl px-5 py-2" />
        )}
      </div>

      {/* Current / next batch info */}
      <div className={`${innerBg} rounded-2xl p-4 flex flex-col justify-center gap-2 shrink-0`}>
        {active ? (
          <>
            <div>
              <div className={`text-4xl font-bold leading-tight ${textPrimary} break-words line-clamp-2 sm:text-5xl`} dir="auto" title={active.blend_name}>{active.blend_name}</div>
              <div className={`text-base font-semibold mt-1 ${textMuted}`} dir="rtl">
                <bdi>{active.quantity}</bdi> {active.unit === 'kg' ? 'ק״ג' : active.quantity === 1 ? 'Batch' : 'Batches'}
                {active.batch_weight_kg != null && <span className="mr-2">· <bdi>{active.batch_weight_kg}</bdi> ק״ג לאצווה</span>}
              </div>
              {active.loading_worker_name && (
                <div className="mt-2 text-sm font-bold text-teal-100">👤 העמסה: {active.loading_worker_name}</div>
              )}
            </div>
            <div className="h-5 w-full rounded-full overflow-hidden bg-black/30">
              <div
                className="h-full rounded-full transition-all duration-1000 bg-white/60"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
          </>
        ) : isReady && nextBatch ? (
          <>
            <div>
              <div className="text-xs text-blue-300/70 uppercase tracking-widest font-bold mb-2">הבא בתור</div>
              <div className="text-4xl font-bold leading-tight text-white break-words line-clamp-2 sm:text-5xl" dir="auto" title={nextBatch.blend_name}>{nextBatch.blend_name}</div>
              <div className="text-base font-semibold mt-1 text-white/70" dir="rtl">
                <bdi>{nextBatch.quantity}</bdi> {nextBatch.unit === 'kg' ? 'ק״ג' : nextBatch.quantity === 1 ? 'Batch' : 'Batches'}
                {nextBatch.batch_weight_kg != null && <span className="mr-2">· <bdi>{nextBatch.batch_weight_kg}</bdi> ק״ג לאצווה</span>}
              </div>
              {nextBatch.loading_worker_name && (
                <div className="mt-2 text-sm font-bold text-teal-200">👤 העמסה: {nextBatch.loading_worker_name}</div>
              )}
            </div>
            <div className="flex items-center justify-center gap-3 py-1">
              <span className="inline-block w-3 h-3 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-sm text-blue-200/80 font-semibold">ממתין להתחלת קלייה</span>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-500 text-base">אין קלייה פעילה</div>
        )}
      </div>

      <OvenMetrics remaining={remaining} beanTemp={beanTemp} timerColor={timerColor} />

      {/* Today's counts */}
      <div className="flex gap-4">
        <CountChip label="ממתינים" value={queuedCount} color="text-blue-400" />
        <CountChip label="בביצוע" value={inProgressCount} color="text-yellow-400" />
        <CountChip label="הושלמו" value={doneCount} color="text-green-400" />
      </div>
    </div>
  );
}

function OvenStatus() {
  const { todayBatches, strayOccupants, ovenQueues, refreshPlan, allSensorReadings } = useAppData();
  const batches = [...todayBatches, ...strayOccupants];
  const [modbusByOven, setModbusByOven] = useState<Record<string, string | null>>({});
  const [nowMs, setNowMs] = useState(Date.now());

  const ovenTemperature = (mid: string, address: string, name: string): LatestSensorValue =>
    latestSensorValue(allSensorReadings, modbusByOven[mid], address, name, nowMs);

  // Oven → Modbus device mapping (rarely changes; load once).
  useEffect(() => {
    OVEN_IDS.forEach(async (mid) => {
      try {
        const config = await getOvenConfig(mid);
        setModbusByOven(prev => ({ ...prev, [mid]: config.modbus_device_id ?? null }));
      } catch {
        setModbusByOven(prev => ({ ...prev, [mid]: null }));
      }
    });
  }, []);

  // 1s tick so running countdowns advance between polls.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex flex-col h-full min-h-0 gap-3 -mx-4 sm:-mx-6 lg:-mx-8 -my-2 px-4 sm:px-6 lg:px-8 py-4"
         style={{ background: '#1a1008' }}>
      {/* Brand header */}
      <div className="flex items-center gap-3 shrink-0 px-1">
        <img src="/icon.png" alt="דאדא" className="h-9 w-auto rounded-xl border border-amber-800/40 shadow-md" />
        <span className="text-amber-200/70 text-base font-semibold tracking-widest uppercase"
              style={{ fontFamily: "'Frank Ruhl Libre', serif", letterSpacing: '0.2em' }}>
          דאדא
        </span>
        <span className="text-amber-800/50 text-sm mx-1">|</span>
        <span className="text-amber-200/50 text-sm font-medium">סטטוס תנורים</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 auto-rows-fr gap-6 flex-1 min-h-0">
        {OVEN_IDS.map((mid) => (
          <OvenCard
            key={mid}
            mid={mid}
            batches={batches}
            ovenQueues={ovenQueues}
            beanTemp={ovenTemperature(mid, '400003', 'Mbus_BeansTemp@Prog1')}
            nowMs={nowMs}
            onFinish={async () => {
              const activeBatch = batches.find(b => b.machine_id === mid && b.status === 'in-progress');
              if (!activeBatch) return;
              await finishBatch(activeBatch.id);
              refreshPlan();
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default OvenStatus;
